import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button, Flex, toast } from "@jsq/ds/core";
import { Form, useFormContext } from "@jsq/ds/forms";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { Sizes } from "@jsq/ds/styles";
import { useFeatureFlag } from "@jsq/flags-react";
import { addAction } from "@jsq/observability";
import { globalEnv } from "~/global-env";
import {
  AgentJobStatus,
  DocumentStatus,
  ComplianceCaseTypeEnum,
} from "~/graphql/supergraphEnums";
import {
  useUpdateComplianceDocumentDetailsMutation,
  useGetDocumentLazyQuery,
} from "~/graphql/supergraphHooks";
import {
  type GetComplianceCaseQuery,
  type DocumentExtractionDifference,
} from "~/graphql/supergraphTypes";
import {
  useComplianceAiExtraction,
  useIsFatcaCrsEnabledForCase,
  usePicrRevampFeatureFlag,
} from "~/pages/Compliance/flags";
import { isFatcaCaseType } from "~/pages/Compliance/utils";
import { SPACING } from "~/styles/variables";
import { extractChildProfiles } from "../../utils";
import { INITIAL_VALUES } from "./constants";
import { DocumentExpiredBanner } from "./DocumentExpiredBanner";
import { DocumentStatusCard } from "./DocumentStatusCard";
import { ProfileAwareFormContent } from "./ProfileAwareFormContent";
import { RequiredFieldsMissingBanner } from "./RequiredFieldsMissingBanner";
import { createSchema } from "./schema";
import * as styles from "./styles_vanilla.css";
import { rightSidebarContainer } from "./styles_vanilla.css";
import { TaxDetailsCard } from "./TaxDetailsCard";
import {
  ExtractedDataResult,
  DocumentTypes,
  FormValues,
  ProfileTypes,
  DocumentType,
} from "./types";
import {
  buildInitialValues,
  buildMetadata,
  getNextIncompleteDocumentId,
  shouldShowSaveAndExit,
  findFirstMatchingProfileFromExtractedData,
  isTerminalExtractionStatus,
} from "./utils";

interface AIBorderEffectProps {
  documentMap: Map<number, DocumentType>;
  documentId: number | undefined;
  filteredDocument: DocumentType | undefined;
  showAIIndication: string;
  setShowAIIndication: (className: string) => void;
}

const AIBorderEffect = ({
  documentMap,
  documentId,
  filteredDocument,
  showAIIndication,
  setShowAIIndication,
}: AIBorderEffectProps) => {
  const { dirty } = useFormContext<FormValues>();
  const extractedData = documentMap.get(documentId ?? 0)
    ?.agentExtractedData as ExtractedDataResult;
  const hasBaseType = filteredDocument?.documentPurpose?.baseType;
  const documentType = extractedData?.DOCUMENT_TYPE;

  useEffect(() => {
    let newClassName = "";

    // Don't show AI border if form is dirty, document has a base type, or document type is "other"
    if (dirty || hasBaseType || documentType === "other") {
      newClassName = "";
    }
    // Show AI border if we have extracted document type but no base type
    else if (documentType && !hasBaseType) {
      newClassName = styles.AIBorder;
    }
    // Default to no border
    else {
      newClassName = "";
    }

    if (newClassName !== showAIIndication) {
      setShowAIIndication(newClassName);
    }
  }, [dirty, hasBaseType, documentType, showAIIndication, setShowAIIndication]);

  return null;
};

interface Props {
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
  documents: DocumentTypes;
}

const DocumentDetailsRightSidebar = ({ complianceCase, documents }: Props) => {
  const aiExtractionEnabled = useComplianceAiExtraction();
  const isFatcaCrsEnabled = useIsFatcaCrsEnabledForCase(
    complianceCase?.caseType as ComplianceCaseTypeEnum | undefined,
    complianceCase?.entity?.complianceSettings?.fatcaCrsEnabled ?? false,
  );
  const [getDocument] = useGetDocumentLazyQuery();
  const [documentMap, setDocumentMap] = useState<Map<number, DocumentType>>(new Map());
  const [isExtracting, setIsExtracting] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const firstDocument = documents[0];
  const documentIdStr = searchParams.get("documentId");
  const documentId = documentIdStr ? parseInt(documentIdStr) : firstDocument?.id;

  const getDocumentExtractionStatus = async (documentId: number) => {
    // Check existing document map first
    const existingDocument = documentMap.get(documentId);
    const existingStatus = existingDocument?.agentJobStatus;

    if (
      existingDocument &&
      (existingStatus == null || existingStatus !== AgentJobStatus.Started)
    ) {
      return {
        document: existingDocument,
        extractionStatus: existingStatus,
      };
    }

    // If not found or status is not ready, make new call
    const { data: documentData } = await getDocument({
      variables: { documentId },
      fetchPolicy: "network-only",
    });
    const document = documentData?.document;

    return {
      document,
      extractionStatus: document?.agentJobStatus,
    };
  };

  const getExtractedData = useCallback(
    async (
      documentId: number,
      maxAttempts = 50,
      attempt = 1,
    ): Promise<ExtractedDataResult | null> => {
      try {
        if (attempt > maxAttempts) {
          setIsExtracting(false);
          return null;
        }

        const { document, extractionStatus } = await getDocumentExtractionStatus(documentId);

        setIsExtracting(true);
        if (!isTerminalExtractionStatus(extractionStatus)) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          return getExtractedData(documentId, maxAttempts, attempt + 1);
        }

        if (document && document.agentExtractedData) {
          // Store the document with extracted data in the map
          setDocumentMap((prev) => new Map(prev).set(documentId, document as DocumentType));
          setIsExtracting(false);
          return document.agentExtractedData as ExtractedDataResult;
        }
        setIsExtracting(false);
        return null;
      } catch {
        setIsExtracting(false);
        return null;
      }
    },
    [documentId],
  );

  useEffect(() => {
    if (documentId && !aiExtractionEnabled) {
      void getExtractedData(documentId);
    }
  }, [documentId]);

  enum TransitionState {
    SubmitAndStay,
    SubmitAndExit,
    SubmitAndNext,
  }
  const [transitionState, setTransitionState] = useState(TransitionState.SubmitAndStay);
  const isPicrDocumentUploadEnabled = useFeatureFlag("picrDocumentUploadEnabled", false);
  const picrRevampEnabled = usePicrRevampFeatureFlag();

  const [updateDocumentDetails] = useUpdateComplianceDocumentDetailsMutation({
    refetchQueries: ["GetComplianceCase"],
    onCompleted: () => toast.success("Updated document details successfully"),
    onError: (e) => toast.error(e.message),
  });
  const history = useHistory();
  const filteredDocument = documents.find((doc) => doc.id === documentId);
  const checkIfDocumentIsLinkedToCase = useMemo(
    () => Boolean(complianceCase?.documents?.find((doc) => doc.id === documentId)),
    [complianceCase?.documents, documentId],
  );

  const isPICR =
    complianceCase?.caseType === ComplianceCaseTypeEnum.PaymentInstructionChangeReview;
  const isTaxDetailsApplicableCase = isFatcaCaseType(
    complianceCase?.caseType as ComplianceCaseTypeEnum | undefined,
  );
  const showProfileDetails = !isPICR || picrRevampEnabled;

  // Add check for valid profiles
  const hasValidProfiles = useMemo(() => {
    const allProfiles = extractChildProfiles(complianceCase, picrRevampEnabled);
    return (
      allProfiles.length > 0 &&
      allProfiles.every((profile: { id: number } | null) => profile !== null && profile.id)
    );
  }, [complianceCase, picrRevampEnabled]);

  const isReadonly = useMemo(() => {
    if (isExtracting) {
      return true;
    }

    if (isPICR && !isPicrDocumentUploadEnabled) {
      return true;
    }

    if (complianceCase?.readonly) {
      return true;
    }

    if (isPICR) {
      return !checkIfDocumentIsLinkedToCase;
    }

    if (hasValidProfiles) {
      return !checkIfDocumentIsLinkedToCase;
    }

    return true;
  }, [
    checkIfDocumentIsLinkedToCase,
    complianceCase?.readonly,
    hasValidProfiles,
    isExtracting,
    isPICR,
    isPicrDocumentUploadEnabled,
  ]);
  const submitDocumentDetails = async (
    values: FormValues,
    dataDifferences: DocumentExtractionDifference[] | null,
  ) => {
    try {
      const profileId =
        values.relatedProfile && values.relatedProfile !== ""
          ? Number(values.relatedProfile)
          : null;
      const updateInfo = {
        profileId,
        documentPurpose: values.documentType,
        documentStatus:
          values.documentStatus === "verified"
            ? DocumentStatus.Verified
            : DocumentStatus.Invalid,
        metadata: buildMetadata(values, filteredDocument?.metadata),
        documentExtractionDifferences: dataDifferences,
      };

      return await updateDocumentDetails({
        variables: {
          caseId: complianceCase?.id ?? 0,
          documentId: documentId ?? 0,
          updateInfo,
        },
      });
    } catch {
      // do nothing
    }
  };

  const getDataDifferences = (values: FormValues): DocumentExtractionDifference[] | null => {
    const extractedData = documentMap.get(documentId ?? 0)
      ?.agentExtractedData as ExtractedDataResult;
    if (!extractedData) return null;
    const differencesArray: DocumentExtractionDifference[] = [];

    // Compare document type
    if (
      extractedData.DOCUMENT_TYPE &&
      extractedData.DOCUMENT_TYPE !== values.secondaryDocumentType
    ) {
      differencesArray.push({
        field: "documentType",
        extracted: extractedData.DOCUMENT_TYPE,
        selected: values.secondaryDocumentType || "",
      });
    }

    // Compare profile selection
    const extractedProfileId = findFirstMatchingProfileFromExtractedData(
      (complianceCase?.profiles ?? null) as ProfileTypes,
      extractedData,
    );
    const selectedProfileId = Number(values.relatedProfile);

    if (extractedProfileId && extractedProfileId !== selectedProfileId) {
      differencesArray.push({
        field: "profile",
        extracted: extractedProfileId.toString(),
        selected: selectedProfileId.toString(),
      });
    }

    // Compare expiry date
    if (extractedData.EXPIRATION_DATE && values.expiryDate) {
      const extractedDate = new Date(extractedData.EXPIRATION_DATE).toISOString().split("T")[0];
      const selectedDate = values.expiryDate.toISOString().split("T")[0];

      if (extractedDate !== selectedDate) {
        differencesArray.push({
          field: "expiryDate",
          extracted: extractedDate,
          selected: selectedDate,
        });
      }
    }

    return differencesArray.length > 0 ? differencesArray : null;
  };

  const handleSubmit = async (values: FormValues) => {
    const dataDifferences = getDataDifferences(values);
    const updateDocumentDetailsResult = await submitDocumentDetails(values, dataDifferences);
    const extractedData = documentMap.get(documentId ?? 0)
      ?.agentExtractedData as ExtractedDataResult;

    if (dataDifferences) {
      // Track when user modifies extracted data
      addAction("document_details_save", {
        caseId: complianceCase?.id,
        documentId,
        dataDifferences,
        extractedDocumentType: extractedData?.DOCUMENT_TYPE,
      });
    } else {
      // Track when extracted data is used without modifications
      addAction("extracted_data_usage_success", {
        caseId: complianceCase?.id,
        documentId,
        extractedDocumentType: extractedData?.DOCUMENT_TYPE,
      });
    }

    // If the transition state was set, transition to the appropriate page
    if (updateDocumentDetailsResult && !updateDocumentDetailsResult.errors) {
      if (transitionState == TransitionState.SubmitAndExit) {
        exitDocManagement();
      } else if (transitionState == TransitionState.SubmitAndNext) {
        navigateToNextDoc();
      }
    }
  };

  /**
   * Navigates the user to the next available incomplete document.
   */
  const navigateToNextDoc = () => {
    // Move to the next available (status that not yet completed) document
    const nextDocId = getNextIncompleteDocumentId(documentId, documents);
    if (nextDocId >= 0) {
      history.push(
        `${globalEnv.complianceRoute}/case/${complianceCase?.id}/document-management?documentId=${nextDocId}`,
      );
    }
  };

  /**
   * Navigates the user back to the documents tab out of the document management page.
   * If the user came from the escalations table with filter parameters,
   * preserve those parameters when navigating back.
   */
  const exitDocManagement = useCallback(() => {
    const storedFilters = sessionStorage.getItem("escalationFilters") || "";

    if (storedFilters && complianceCase?.id) {
      const params = new URLSearchParams(storedFilters);
      const hasEscalationFilters = params.has("sourceCaseId") || params.has("clientId");

      if (hasEscalationFilters) {
        const targetPath = `${globalEnv.complianceRoute}/escalations/${complianceCase.id}/details`;
        history.push({
          pathname: targetPath,
          search: storedFilters,
        });
        return;
      }
    }

    history.push(`${globalEnv.complianceRoute}/case/${complianceCase?.id}/documents`);
  }, [history, complianceCase?.id]);

  const [showAIIndication, setShowAIIndication] = useState("");
  const fatcaHasSelectionRef = useRef(false);
  const validationSchema = useMemo(() => {
    return createSchema(
      complianceCase?.caseType,
      picrRevampEnabled,
      isFatcaCrsEnabled,
      complianceCase?.investingProfile?.id,
      fatcaHasSelectionRef,
    );
  }, [
    complianceCase?.caseType,
    picrRevampEnabled,
    isFatcaCrsEnabled,
    complianceCase?.investingProfile?.id,
  ]);

  const initialValues = useMemo(() => {
    const values =
      filteredDocument !== undefined
        ? buildInitialValues(
            filteredDocument,
            (complianceCase?.profiles ?? null) as ProfileTypes,
            (documentMap.get(documentId ?? 0)?.agentExtractedData as ExtractedDataResult) ??
              null,
          )
        : INITIAL_VALUES;
    return values;
  }, [filteredDocument, complianceCase?.profiles, documentMap, documentId]);

  const caseInfo = complianceCase?.caseType
    ? {
        type: complianceCase.caseType,
        description: complianceCase.escalations?.[0]?.description,
      }
    : undefined;

  return (
    <Flex className={rightSidebarContainer}>
      <ColumnContainer style={{ overflowY: "auto", overscrollBehavior: "contain" }}>
        <Column size="md">
          <Form
            enableReinitialize
            onSubmit={handleSubmit}
            initialValues={initialValues}
            submitOnlyChangedValues={false}
            validationSchema={validationSchema}
            shouldResetOnSubmit={transitionState !== TransitionState.SubmitAndStay}
          >
            <AIBorderEffect
              documentMap={documentMap}
              documentId={documentId}
              filteredDocument={filteredDocument}
              showAIIndication={showAIIndication}
              setShowAIIndication={setShowAIIndication}
            />
            {isFatcaCrsEnabled && isTaxDetailsApplicableCase && (
              <Flex flexDirection="column" gap={Sizes.MD} className={styles.bannersContainer}>
                <RequiredFieldsMissingBanner />
                <DocumentExpiredBanner />
              </Flex>
            )}
            <ProfileAwareFormContent
              complianceCase={complianceCase}
              picrRevampEnabled={picrRevampEnabled}
              readonly={isReadonly}
              isExtracting={isExtracting}
              filteredDocument={filteredDocument}
              documentId={documentId ?? 0}
              showProfileDetails={showProfileDetails}
              showAIIndication={showAIIndication}
              isPICR={isPICR}
              caseInfo={caseInfo}
            />
            {isFatcaCrsEnabled && isTaxDetailsApplicableCase && (
              <TaxDetailsCard
                readonly={isReadonly}
                documentId={documentId}
                taxDocument={filteredDocument}
                investingProfileId={complianceCase?.investingProfile?.id}
              />
            )}
            {isFatcaCrsEnabled && isTaxDetailsApplicableCase && (
              <DocumentStatusCard
                readonly={isReadonly}
                complianceCase={complianceCase}
                documentId={documentId}
                documentName={filteredDocument?.name}
                fatcaHasSelectionRef={fatcaHasSelectionRef}
              />
            )}
            {shouldShowSaveAndExit(documentId, documents) && (
              <Button
                type="submit"
                data-testid="save-and-exit-button"
                disabled={isReadonly}
                onClick={() => {
                  setTransitionState(TransitionState.SubmitAndExit);
                }}
                css={{ marginTop: SPACING.MEDIUM, marginLeft: SPACING.SMALL, float: "right" }}
              >
                Save and exit
              </Button>
            )}
            {!shouldShowSaveAndExit(documentId, documents) && (
              <Button
                type="submit"
                disabled={isReadonly}
                data-testid="save-and-next-button"
                onClick={() => {
                  setTransitionState(TransitionState.SubmitAndNext);
                }}
                css={{ marginTop: SPACING.MEDIUM, marginLeft: SPACING.SMALL, float: "right" }}
              >
                Save and next
              </Button>
            )}
            <Button
              css={{ marginTop: SPACING.MEDIUM, float: "right" }}
              type="submit"
              data-testid="save-button"
              disabled={isReadonly}
              onClick={() => {
                setTransitionState(TransitionState.SubmitAndStay);
              }}
            >
              Save
            </Button>
          </Form>
        </Column>
      </ColumnContainer>
    </Flex>
  );
};

export { DocumentDetailsRightSidebar };
